import React from 'react';
import { Link } from 'react-router-dom';
import { useSettings } from '../App';
import { Button } from './ui/button';
import { Play, Tv, Monitor, Smartphone } from 'lucide-react';

const HomePage = () => {
  const { platformSettings } = useSettings();

  // Use default values if settings are not loaded yet
  const settings = platformSettings || {
    platform_name: "Digital TV Play",
    logo_url: null,
    hero_title: "DIGITAL TV PLAY", 
    hero_subtitle: "Curta os principais canais",
    hero_description: "com os melhores conteúdos",
    hero_text: "Na Digital TV Play você tem acesso à melhor programação ao vivo com qualidade premium.",
    restriction_warning: "⚠️ Plataforma de Acesso Restrito",
    restriction_text: "O cadastro requer aprovação administrativa para garantir qualidade e segurança.",
    background_image_url: null,
    background_color: "#0f172a",
    hero_background_color: null,
    footer_text: null
  };

  const backgroundStyle = {
    backgroundImage: settings.background_image_url 
      ? `url(${settings.background_image_url}), linear-gradient(135deg, rgba(15, 23, 42, 0.8), rgba(30, 58, 138, 0.8), rgba(91, 33, 182, 0.8))`
      : 'linear-gradient(135deg, #0f172a, #1e3a8a, #5b21b6)',
    backgroundColor: settings.hero_background_color || settings.background_color,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundBlendMode: settings.background_image_url ? 'overlay' : 'normal'
  };

  return (
    <div className="min-h-screen" style={backgroundStyle}>
      {/* Header */}
      <header className="relative z-10 px-6 py-4">
        <nav className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-2">
            {settings.logo_url ? (
              <img 
                src={settings.logo_url} 
                alt="Logo"
                className="h-8 w-auto max-w-[200px]"
                onError={(e) => {
                  e.target.style.display = 'none';
                  if (e.target.nextSibling) {
                    e.target.nextSibling.style.display = 'flex';
                  }
                }}
              />
            ) : (
              <div className="flex items-center space-x-2">
                <Tv className="h-8 w-8 text-blue-400" />
                <span className="text-2xl font-bold text-white">{settings.platform_name}</span>
              </div>
            )}
          </div>
          <div className="flex items-center space-x-4">
            <Link to="/login">
              <Button variant="outline" className="text-white border-white hover:bg-white hover:text-gray-900">
                Entrar
              </Button>
            </Link>
            <Link to="/register">
              <Button className="bg-blue-600 hover:bg-blue-700">
                Cadastrar
              </Button>
            </Link>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <main className="relative">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1619235198621-cedf22c02ef5?q=80&w=2000&auto=format&fit=crop)'
          }}
        />
        
        {/* Content */}
        <div className="relative z-10 px-6 py-20">
          <div className="max-w-4xl mx-auto text-center">
            <div className="animate-fade-in">
              <h1 className="text-6xl md:text-7xl font-bold text-white mb-6 leading-tight">
                {settings.hero_title || settings.platform_name}
              </h1>
              <p className="text-xl md:text-2xl text-blue-200 mb-4 font-medium">
                {settings.hero_subtitle}
              </p>
              <p className="text-lg md:text-xl text-white mb-8">
                {settings.hero_description}
              </p>
              <p className="text-lg text-gray-300 mb-12 max-w-2xl mx-auto leading-relaxed">
                {settings.hero_text}
                <br/><br/>
                <span className="text-yellow-300 font-semibold">
                  {settings.restriction_warning}
                </span>
                <br/>
                {settings.restriction_text}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Link to="/login">
                  <Button 
                    size="lg" 
                    className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 text-lg font-semibold rounded-xl animate-glow"
                  >
                    <Play className="mr-2 h-5 w-5" />
                    Começar a Assistir
                  </Button>
                </Link>
                <Link to="/register">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="text-white border-white hover:bg-white hover:text-gray-900 px-8 py-4 text-lg font-semibold rounded-xl"
                  >
                    Solicitar Acesso
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Features Section */}
        <div className="relative z-10 px-6 py-20 bg-black bg-opacity-30">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-16">
              Assista em Qualquer Lugar
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center animate-slide-in">
                <div className="bg-blue-600 bg-opacity-20 rounded-full p-6 w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                  <Monitor className="h-10 w-10 text-blue-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-4">Desktop</h3>
                <p className="text-gray-300">
                  Assista no seu computador com qualidade HD
                </p>
              </div>
              
              <div className="text-center animate-slide-in" style={{ animationDelay: '0.2s' }}>
                <div className="bg-blue-600 bg-opacity-20 rounded-full p-6 w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                  <Tv className="h-10 w-10 text-blue-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-4">Smart TV</h3>
                <p className="text-gray-300">
                  Conecte na sua TV e assista na tela grande
                </p>
              </div>
              
              <div className="text-center animate-slide-in" style={{ animationDelay: '0.4s' }}>
                <div className="bg-blue-600 bg-opacity-20 rounded-full p-6 w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                  <Smartphone className="h-10 w-10 text-blue-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-4">Mobile</h3>
                <p className="text-gray-300">
                  Leve seus canais favoritos para onde for
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="relative z-10 px-6 py-16 text-center">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Pronto para começar?
            </h2>
            <p className="text-lg text-gray-300 mb-8">
              Solicite seu acesso para começar a assistir nossa programação premium
            </p>
            <Link to="/register">
              <Button 
                size="lg" 
                className="bg-blue-600 hover:bg-blue-700 text-white px-12 py-4 text-lg font-semibold rounded-xl"
              >
                Solicitar Acesso Agora
              </Button>
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="relative z-10 bg-black bg-opacity-50 px-6 py-8">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center space-x-2 mb-4">
            {settings.logo_url ? (
              <img 
                src={settings.logo_url} 
                alt="Logo"
                className="h-6 w-auto max-w-[150px]"
                onError={(e) => {
                  e.target.style.display = 'none';
                  if (e.target.nextSibling) {
                    e.target.nextSibling.style.display = 'flex';
                  }
                }}
              />
            ) : (
              <div className="flex items-center space-x-2">
                <Tv className="h-6 w-6 text-blue-400" />
                <span className="text-xl font-bold text-white">{settings.platform_name}</span>
              </div>
            )}
          </div>
          <p className="text-gray-400">
            © 2024 {settings.platform_name}. Todos os direitos reservados.
          </p>
          {settings.footer_text && (
            <p className="text-gray-400 text-sm mt-2">
              {settings.footer_text}
            </p>
          )}
        </div>
      </footer>
    </div>
  );
};

export default HomePage;